import * as React from 'react';

import { useQueue } from '@uidotdev/usehooks';
import { LoaderCircle, LucideProps } from 'lucide-react';
import {
  Button as AriaButton,
  ButtonRenderProps,
  composeRenderProps,
  PressEvent,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';
import { useHotkeys } from 'react-hotkeys-hook';

import { cn, withColor } from '../../../lib/stylingUtils';
import { ComplexKeyboardShortcut } from '../../../types/keyboard';
import { ThemeColor } from '../../../types/theme';
import { Kbd } from '../forms/Kbd';

/**
 * Props for the Button component.
 * Extends AriaButtonProps from react-aria-components and includes styling variants.
 */
export type ButtonProps = AriaButtonProps & {
  variant?:
    | 'solid'
    | 'destructive'
    | 'outline'
    | 'ghost'
    | 'dashed'
    | 'text'
    | 'link'
    | 'subtle';
  size?: 'default' | 'small' | 'large' | 'icon';
  // shadow?: 'solid' | 'none';
  color?: ThemeColor;
  icon?: React.ReactElement<LucideProps & { className?: string }>;
  isLoading?: boolean;
  keyboardShortcut?: ComplexKeyboardShortcut;
  enableShortcutOnFormTags?: boolean;
  enableShortcutOnContentEditable?: boolean;
  ref?: React.RefObject<HTMLButtonElement | null>;
  preventFocus?: boolean;
};

/**
 * Button component with various styling options and accessibility features.
 *
 * Renders a button with customizable appearance and built-in wave animation effect.
 * Built on react-aria-components for robust accessibility support.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Button>Click me</Button>
 *
 * // With icon
 * <Button icon={<PlusIcon />}>Add item</Button>
 *
 * // Icon-only button
 * <Button size="icon" icon={<SearchIcon />}>Search</Button>
 * ```
 *
 * @param props - Button properties
 * @returns Rendered Button component
 */
const Button = ({
  className,
  children,
  preventFocus,
  variant = 'solid',
  // shadow,
  color: colorProp,
  size = 'default',
  icon,
  isLoading: loading,
  keyboardShortcut,
  enableShortcutOnFormTags = true,
  enableShortcutOnContentEditable = true,
  ref,
  onFocus,
  ...props
}: ButtonProps) => {
  const color = colorProp ?? (variant === 'outline' ? 'neutral' : 'primary');

  const {
    add: addWaveElement,
    remove: removeWaveElement,
    queue: pingElements,
  } = useQueue<React.ReactNode>([]);

  useHotkeys(
    keyboardShortcut || '',
    (e) => {
      if (typeof props.onPress === 'function') {
        props.onPress(e as never);
      }
      if (typeof props.onClick === 'function') {
        props.onClick(e as never);
      }
    },
    {
      enabled: !!keyboardShortcut && !props.isDisabled,
      enableOnFormTags: enableShortcutOnFormTags,
      enableOnContentEditable: enableShortcutOnContentEditable,
      preventDefault: true,
    },
  );

  const handlePress = (e: PressEvent) => {
    addWaveElement(
      <div
        key={Date.now()}
        className="absolute top-0 left-0 z-[-1] animate-wave rounded-md bg-custom-color"
        onAnimationEnd={removeWaveElement}
      >
        &nbsp;
      </div>,
    );

    props.onPress?.(e);
  };

  // const isVariantWithShadow =
  //   variant === 'solid' || variant === 'outline' || variant === 'dashed';

  const iconSize = size === 'large' ? 18 : size === 'default' ? 16 : 14;

  const handleFocus = React.useCallback(
    (e: React.FocusEvent<Element, Element>) => {
      if (preventFocus) (e.target as HTMLElement).blur();
      else onFocus?.(e as never);
    },
    [preventFocus, onFocus],
  );

  return (
    <AriaButton
      ref={ref}
      style={withColor(color)}
      className={composeRenderProps(className, (className) =>
        // prettier-ignore
        cn(
          '@Button',
          // Base styles
          'relative inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md text-sm capitalize',
          'ring-offset-neutral/10 transition-all',
          // Disabled
          'rac-disabled:pointer-events-none rac-disabled:opacity-20',
          // Focus Visible
          'rac-focus-visible:ring-primary/30 rac-focus-visible:ring-3 rac-focus-visible:outline-none',
          'focus-visible:outline-none',
          // Keyboard shortcut focus
          keyboardShortcut &&
            'rac-focus-visible:ring-primary/30 rac-focus-visible:ring-3 rac-focus-visible:outline-none',
          // Size variants
          size === 'default' && 'h-input px-4 py-1.5',
          size === 'small' && 'h-6 px-3 py-1',
          size === 'large' && 'h-10 px-5 py-2.5',
          size === 'icon' && 'size-8',
          // Shadow
          // (shadow === 'solid' || (!shadow && isVariantWithShadow)) && 'shadow-solid',
          // Variant styles
          variant === 'solid' && 'bg-custom-color rac-hover:bg-custom-color/80 rac-pressed:bg-custom-color text-abs-white',
          variant === 'destructive' && 'bg-failure rac-hover:bg-failure/80 rac-pressed:bg-failure-darker text-abs-white',
          variant === 'outline' && 'border-custom-color text-custom-color rac-hover:border-custom-color/80 rac-hover:text-custom-color/80 rac-pressed:border-custom-color rac-pressed:text-custom-color border bg-white hover:bg-custom-color/5',
          variant === 'outline' && color === 'neutral' && 'border-custom-color/30! text-custom-color/80!',
          variant === 'ghost' && 'border-custom-color text-custom-color rac-hover:border-custom-color/80 rac-hover:text-custom-color/80 rac-pressed:border-custom-color rac-pressed:text-custom-color border bg-transparent',
          variant === 'dashed' && 'bg-neutral/10 border-custom-color text-custom-color rac-hover:border-custom-color/80 rac-hover:text-custom-color/80 rac-pressed:border-custom-color rac-pressed:text-custom-color border border-dashed',
          variant === 'text' && 'text-custom-color rac-hover:bg-custom-color/10 rac-hover:text-custom-color/80 rac-pressed:bg-custom-color/20 rac-pressed:text-custom-color bg-transparent',
          variant === 'link' && 'rac-hover:underline text-custom-color rac-hover:text-custom-color/80 rac-pressed:text-custom-color cursor-pointer bg-transparent underline-offset-4',
          variant === 'subtle' && 'bg-custom-color/10 border border-custom-color/20 text-custom-color hover:bg-custom-color/20 hover:border-custom-color/30',
          className,
        ),
      )}
      {...props}
      isPending={props.isPending || loading}
      isDisabled={props.isDisabled || loading}
      onPress={handlePress}
      onFocus={handleFocus}
    >
      {(
        renderProps: ButtonRenderProps & {
          defaultChildren: React.ReactNode | undefined;
        },
      ) => (
        <div className="flex size-full items-center justify-center gap-1.5">
          {pingElements}
          {!loading &&
            icon &&
            React.cloneElement(icon, {
              size: icon.props.size ?? iconSize,
              className: cn(icon.props.className, 'shrink-0'),
            })}
          {loading && (
            <span
              className={cn('animate-loading-icon', size !== 'icon' && 'mr-1')}
            >
              <LoaderCircle size={14} className="animate-spin" />
            </span>
          )}
          {size !== 'icon' &&
            (typeof children === 'function' ? children(renderProps) : children)}
          {size !== 'icon' && keyboardShortcut && (
            <Kbd variant={variant} shortcut={keyboardShortcut} />
          )}
        </div>
      )}
    </AriaButton>
  );
};

export { AriaButton as UnstyledButton, Button };
